import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, Pause, Play, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { cn } from '@/lib/utils/cn';
import { useBulkCampaignAction, useRunningPausedCampaigns } from '../api';
import { CampaignStatusBadge } from '../components/CampaignStatusBadge';
import { STATUS_MAP, type CampaignRow } from '../types';

type Filter = 'running' | 'paused';

export function CampaignOperationsPage() {
  const [filter, setFilter] = useState<Filter>('running');
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const canView = usePermission('Manage Campaign.Campaign Operations', BASE_PERMISSION.VIEW);

  const query = useRunningPausedCampaigns(filter);
  const bulk = useBulkCampaignAction();

  const [pending, setPending] = useState<{ ids: string[]; clear: () => void } | null>(null);

  const isRunning = filter === 'running';
  const actionLabel = isRunning ? 'Pause' : 'Resume';
  const ActionIcon = isRunning ? Pause : Play;

  const columns = useMemo<ColumnDef<CampaignRow>[]>(
    () => [
      {
        accessorKey: 'campaignId',
        header: 'Campaign ID',
        size: 120,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.campaignId}
          </span>
        ),
      },
      { accessorKey: 'campaignName', header: 'Campaign' },
      {
        accessorKey: 'campaignStatus',
        header: 'Status',
        size: 160,
        cell: ({ row }) => <CampaignStatusBadge status={row.original.campaignStatus} />,
      },
    ],
    [],
  );

  const confirmAction = () => {
    if (!pending) return;
    bulk.mutate(
      {
        campaignIds: pending.ids.join(','),
        status: isRunning ? (STATUS_MAP.Pausing as number) : (STATUS_MAP.Resuming as number),
        actionOn: isSubOrSuper ? 'superadmin' : 'user',
      },
      {
        onSuccess: (res) => {
          toast.success(
            res?.message ??
              `${actionLabel} request submitted for ${pending.ids.length} campaign(s)`,
          );
          pending.clear();
          setPending(null);
          query.refetch();
        },
        onError: () => {
          toast.error('Failed to perform bulk action.');
          setPending(null);
        },
      },
    );
  };

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Campaign Operations"
        description="Bulk pause or resume in-flight campaigns."
        actions={
          <Button
            variant="outline"
            size="icon"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            aria-label="Refresh"
          >
            <RefreshCw
              className={cn('h-4 w-4', query.isFetching && 'animate-spin')}
            />
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>
            Show campaigns currently {filter === 'running' ? 'running' : 'paused'} so
            you can {actionLabel.toLowerCase()} them in bulk.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex max-w-xs flex-col gap-1.5">
            <Label htmlFor="filter">Campaigns</Label>
            <Select value={filter} onValueChange={(v) => setFilter(v as Filter)}>
              <SelectTrigger id="filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="running">Running</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isRunning && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>This does not include continuous campaigns.</span>
        </div>
      )}

      <DataTable<CampaignRow, unknown>
        tableId={`campaigns:operations:${filter}:v1`}
        columns={columns}
        data={query.data ?? []}
        isLoading={query.isLoading}
        searchPlaceholder="Search campaigns…"
        emptyTitle={`No ${filter} campaigns`}
        emptyDescription={
          isRunning
            ? 'Nothing is running right now. Switch to Paused to resume something.'
            : 'No paused campaigns to resume.'
        }
        getRowId={(row) => String(row.campaignId)}
        exportFilename={`${filter}-campaigns.csv`}
        pageSize={10}
        enableRowSelection
        renderSelectionActions={(ids, clear) => (
          <Button
            size="sm"
            onClick={() => setPending({ ids, clear })}
            disabled={ids.length === 0 || bulk.isPending}
          >
            <ActionIcon className="h-4 w-4" />
            {actionLabel} {ids.length}
          </Button>
        )}
      />

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title={`Confirm ${actionLabel.toLowerCase()}`}
        description={
          <>
            Are you sure you want to {actionLabel.toLowerCase()}{' '}
            <span className="font-medium text-foreground">
              {pending?.ids.length ?? 0}
            </span>{' '}
            campaign{pending?.ids.length === 1 ? '' : 's'}?
          </>
        }
        confirmLabel={actionLabel}
        loading={bulk.isPending}
        onConfirm={confirmAction}
      />
    </FillPage>
  );
}
